/**
 * CaseRail — the case list (left, 280 px): asset, trigger, status chip, ticking elapsed time and a
 * pulsing dot on cases the agent opened itself. Schedule cases also show the scenario clock.
 * The footer starts a line replay. Asset labels come from the `assetLabel` prop.
 */

import { useEffect, useState } from 'react';
import {
  LoaderCircle, Hand, Pause, CircleCheck, CircleX, TriangleAlert, Play,
} from 'lucide-react';
import SpecialistDot from './SpecialistDot.jsx';
import {
  caseStatusInfo, caseElapsed, formatElapsed, scenarioClockText, isAgentOpened, oneLine,
} from './agentUi.js';
import './CaseRail.css';

const STATUS_ICON = {
  working: LoaderCircle, waiting: Hand, paused: Pause, done: CircleCheck, cancelled: CircleX, failed: TriangleAlert,
};

/** Status chip: icon + label, never colour alone. */
export function StatusChip({ status }) {
  const info = caseStatusInfo(status);
  const ChipIcon = STATUS_ICON[status] ?? CircleCheck;
  return (
    <span className={`crail__status is-${info.tone}`}>
      <ChipIcon size={11} strokeWidth={2} aria-hidden="true" className={status === 'working' ? 'crail__spin' : undefined} />
      {info.label}
    </span>
  );
}

function useNow(ms = 1000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(timer);
  }, [ms]);
  return now;
}

export default function CaseRail({
  cases = {}, order = [], activeCaseId = null, specialists = {}, assetLabel = (id) => id,
  onOpen, onStartReplay, scenarios = [], replayBusy = false,
  /** Empty-list hint; the vertical supplies its own wording (e.g. its wake phrase). */
  emptyHint = 'No cases yet. Start the line replay.',
}) {
  const now = useNow(1000);
  const [scenario, setScenario] = useState('');
  const chosen = scenario || scenarios[0]?.id || '';

  return (
    <aside className="crail" aria-label="Cases">
      <div className="crail__head">
        <span className="crail__kicker">Cases</span>
        <span className="crail__count">{order.length}</span>
      </div>

      <ul className="crail__list">
        {order.length === 0 && (
          <li className="crail__empty">{emptyHint}</li>
        )}
        {order.map((id) => {
          const c = cases[id];
          if (!c) return null;
          const clock = scenarioClockText(c, now);
          const triggerText = c.trigger?.fault_id
            ? `${c.trigger.fault_id} · ${c.trigger.text ?? ''}`
            : c.trigger?.text ?? '';
          return (
            <li key={id}>
              <button
                type="button"
                className={`crail__case${id === activeCaseId ? ' is-active' : ''}`}
                aria-current={id === activeCaseId ? 'true' : undefined}
                onClick={() => onOpen?.(id)}
              >
                <div className="crail__row">
                  {isAgentOpened(c) && <span className="crail__pulse" title="Opened by the agent" aria-label="Opened by the agent" />}
                  <span className="crail__asset">{assetLabel(c.asset)}</span>
                  <span className="crail__ref">{c.ref}</span>
                </div>
                <div className="crail__title">{oneLine(c.title, 80)}</div>
                {triggerText && <div className="crail__trigger">{oneLine(triggerText, 90)}</div>}
                <div className="crail__row crail__meta">
                  <StatusChip status={c.status} />
                  <SpecialistDot specialist={specialists[c.specialist]} size={6} />
                  <span className="crail__elapsed" title="Elapsed">{formatElapsed(caseElapsed(c, now))}</span>
                </div>
                {clock && <div className="crail__clock">{clock} scenario time</div>}
              </button>
            </li>
          );
        })}
      </ul>

      <div className="crail__foot">
        {scenarios.length > 1 && (
          <select
            className="crail__select"
            value={chosen}
            onChange={(e) => setScenario(e.target.value)}
            aria-label="Scenario to replay"
          >
            {scenarios.map((s) => <option key={s.id} value={s.id}>{s.title || s.id}</option>)}
          </select>
        )}
        <button
          type="button"
          className="crail__replay"
          disabled={replayBusy || typeof onStartReplay !== 'function'}
          onClick={() => onStartReplay?.(chosen || null)}
        >
          <Play size={13} aria-hidden="true" /> Start line replay
        </button>
      </div>
    </aside>
  );
}
